// src/services/SurveyService.js
import API_CONFIG from "./ApiConfig";

const SurveyService = {
  /**
   * Fetches a user's previous responses for a specific survey
   *
   * @param {string} surveyId - The ID of the survey
   * @param {string} token - Firebase authentication token
   * @returns {Promise<Object>} - Survey response data if it exists
   */
  getSurveyResponsesBySurveyId: async (surveyId, token) => {
    try {
      if (!surveyId || !token) {
        throw new Error("Survey ID and authentication token are required");
      }

      const url = `${API_CONFIG.BASE_URL}/api/surveys/response/${surveyId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Headers":
            "Origin, X-Requested-With, Content-Type, Accept, x-user-token",
        },
      });

      // If no responses exist, the API might return 404
      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Server responded with status: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching survey responses:", error);
      // Re-throw to allow the component to handle "no responses" scenario
      throw error;
    }
  },
  /**
   * Fetch a survey by ID
   * @param {string} surveyId - The ID of the survey to fetch
   * @param {string} firebaseToken - Authentication token
   * @returns {Promise<Object>} - Survey data
   */
  getSurveyById: async (surveyId, firebaseToken) => {
    try {
      const url = `${API_CONFIG.BASE_URL}/api/surveys/${surveyId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch survey: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching survey:", error);
      throw error;
    }
  },

  /**
   * Submit survey responses
   * @param {string} surveyId - The ID of the survey
   * @param {string} userId - User ID
   * @param {Array} responses - Array of question responses
   * @param {string} firebaseToken - Authentication token
   * @returns {Promise<Object>} - Submission result
   */
  submitSurveyResponses: async (surveyId, userId, responses, firebaseToken) => {
    try {
      const url = `${API_CONFIG.BASE_URL}/api/nationwide/survey`;

      const payload = {
        surveyTemplateId: surveyId,
        userId: userId,
        responses: responses,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit survey: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting survey responses:", error);
      throw error;
    }
  },  
};

export default SurveyService;
